import { useCallback, useMemo, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import ImageDialog from '@/components/Dialog/ImageDialog/ImageDialog';
import { DropZone } from '@/components/DropZone';
import type { FileItem } from '@/components/DropZone/types';
import Icon from '@/components/Icon/Icon';
import { HStack } from '@/components/Layout';
import { Text } from '@/components/Typography/Text';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { SecureGuard } from '@/features/auth/components/SecureGuard';
import { useDownloadConfidentialFile } from '@/features/highly-confidential';
import type { FileValue } from '@/features/section-attribute/types';
import { defaultNS, useTranslation } from '@/lib/i18n';
import { CONFIDENTIAL_FILE_ICON, isHighlyConfidentialFile } from '@/lib/security/confidential-utils';
import { CFormItemBase, type CFormItemProps, buildCFormItem } from '../CFormItem';
type UploadedFile = FileValue;
type CFormFileValue = FileValue | File;
type Props = CFormItemProps & {
  value: CFormFileValue[];
  onChange?: (value?: CFormFileValue[]) => void;
  title?: string;
  showFileName?: boolean;
  /** 受け付ける MIME タイプ（DropZone の accept に渡す。省略時はすべて許可） */
  accept?: {
    [key: string]: string[];
  };
  /** 最大ファイルサイズ（バイト）。省略時は無制限 */
  maxSize?: number;
  /** 最大ファイル数。省略時は1 */
  maxFiles?: number;
};

/**
 * ファイル名からPDFかどうかを判定する
 */
const isPdfFile = (fileName: string | undefined): boolean => {
  if (!fileName) return false;
  return fileName.toLowerCase().endsWith('.pdf');
};

/**
 * FileValue を DropZone の FileItem に変換する
 */
function valueToFileItem(value: NonNullable<CFormFileValue>): FileItem {
  if (value instanceof File) {
    return {
      id: `${value.name}_${value.size}_${value.lastModified}`,
      file: value,
      name: value.name,
      size: value.size
    };
  }
  return {
    id: value.id ?? `file-${value.originalname}-${value.size}`,
    file: {
      url: value.s3Url,
      type: value.mimetype
    },
    name: value.originalname,
    size: value.size
  };
}

/**
 * Card-based Form / FileItem
 * ファイル（主に画像）を表示し、クリックでImageDialogを開く
 * PDFファイルの場合はダウンロードリンクを表示
 * onChange を指定すると編集モードでファイル選択（DropZone）を表示する
 */
export const CFormFileItem = buildCFormItem((props: Props) => {
  const {
    value,
    title,
    showFileName = true,
    onChange,
    accept,
    maxSize,
    maxFiles,
    readonly
  } = props;
  const {
    t
  } = useTranslation(defaultNS, {
    keyPrefix: 'common.fileItem'
  });
  const {
    t: tHelp
  } = useTranslation(defaultNS, {
    keyPrefix: 'features.cForm.fileItem'
  });
  const [open, setOpen] = useState(false);
  const [isSecured, setIsSecured] = useState(false);
  const [fileDownload, setFileDownload] = useState<() => void>();
  const onDrop = useCallback((acceptedFiles: File[]) => {
    onChange?.((maxFiles ?? 0) <= 1 ? acceptedFiles : [...value, ...acceptedFiles]);
  }, [onChange, value, maxFiles]);
  const {
    getRootProps,
    getInputProps,
    isDragActive
  } = useDropzone({
    onDrop,
    multiple: (maxFiles ?? 0) > 1,
    accept,
    maxSize,
    maxFiles,
    disabled: readonly,
    noClick: false
  });
  const fileItems: FileItem[] = useMemo(() => value.map(valueToFileItem), [value]);
  const handleRemoveFile = useCallback((id: string) => {
    onChange?.(value.filter(file => valueToFileItem(file).id !== id));
  }, [onChange, value]);
  const helpText = useMemo(() => {
    const multiple = (maxFiles ?? 1) > 1;
    const effectiveMaxFiles = maxFiles ?? 1;
    const effectiveMaxSize = maxSize ?? 0;
    const acceptedMimeTypes = accept ? Object.keys(accept) : [];
    const acceptedExtensions = accept ? [...new Set(Object.values(accept).flat())] : [];
    const parts: string[] = [];
    if (multiple && effectiveMaxFiles > 1) {
      parts.push(tHelp('maxFiles', {
        count: effectiveMaxFiles
      }));
    }
    if (effectiveMaxSize > 0) {
      const sizeMB = Math.round(effectiveMaxSize / (1024 * 1024));
      parts.push(tHelp('maxFileSize', {
        size: sizeMB
      }));
    }
    if (acceptedMimeTypes.length > 0) {
      const formats = acceptedMimeTypes.map((mime: string) => mime.split('/')[1]?.toUpperCase()).filter((format): format is string => Boolean(format));
      if (formats.length > 0) {
        parts.push(tHelp('supportedFormats', {
          formats: formats.join(', ')
        }));
      }
    } else if (acceptedExtensions.length > 0) {
      parts.push(tHelp('supportedFormats', {
        formats: acceptedExtensions.join(', ')
      }));
    }
    const constraintText = parts.length > 0 ? ` (${parts.join(', ')})` : '';
    return constraintText;
  }, [accept, maxFiles, maxSize, tHelp]);

  // 高機密ファイルダウンロード用フック
  const {
    download: downloadConfidentialFile
  } = useDownloadConfidentialFile();
  const onHighlyConfidentialFileDownload = useCallback((fileInfo: {
    s3Url: string;
    originalName: string;
    mimeType: string;
  }) => {
    setFileDownload(() => () => {
      downloadConfidentialFile({
        s3Url: fileInfo.s3Url,
        originalName: fileInfo.originalName,
        mimeType: fileInfo.mimeType
      });
    });
    if (isSecured) {
      fileDownload?.();
      return;
    }
    setOpen(true);
  }, [downloadConfidentialFile, fileDownload, isSecured, setOpen]);
  const readFileComponent = (file: UploadedFile) => {
    const isPdf = isPdfFile(file.originalname);
    return <>
        {isHighlyConfidentialFile(file.s3Url) ? <HStack className="gap-2 items-center text-neutral-muted-on-fill">
            <Icon iconName={CONFIDENTIAL_FILE_ICON} className="w-4 h-4 text-neutral-muted-on-fill" />
            {showFileName && <Text variant="labelSemiLarge" className="text-neutral-muted-on-fill">
                {file.originalname || t('unknownFileName')}
              </Text>}
            <Button variant="ghost" size="sm" onClick={() => {
          onHighlyConfidentialFileDownload({
            s3Url: file.s3Url,
            originalName: file.originalname,
            mimeType: file.mimetype
          });
        }}>
              <Icon iconName="Download" />
            </Button>
          </HStack> : isPdf ? <a href={file.s3Url} download={file.originalname} target="_blank" rel="noopener noreferrer" className="text-neutral-on-fill hover:text-neutral-on-fill/80 transition-colors">
            <HStack className="gap-2 items-center">
              <Icon iconName="Download" className="w-5 h-5" />
              {showFileName && <Text variant="labelSemiLarge" className="text-neutral-on-fill">
                  {file.originalname || t('unknownFileName')}
                </Text>}
            </HStack>
          </a> : <HStack className="gap-2 items-center">
            {showFileName && <Text variant="labelSemiLarge" className="text-neutral-on-fill">
                {file.originalname || t('unknownFileName')}
              </Text>}
            <ImageDialog title={title || file.originalname || t('defaultImageTitle')} image={{
          url: file.s3Url,
          alt: file.originalname || t('defaultImageTitle')
        }} />
          </HStack>}
      </>;
  };
  const readComponent = <HStack className="gap-2 items-center">
      {value.length > 0 ? value.filter((file): file is UploadedFile => !(file instanceof File)).map((file, index) => <div key={file.s3Url || index}>{readFileComponent(file)}</div>) : <HStack className="gap-2 items-center text-neutral-muted-on-fill">
          <Icon iconName="ImageIcon" className="w-5 h-5 text-neutral-muted-on-fill" />
          <Text variant="labelSemiLarge" className="text-neutral-muted-on-fill">
            {t('noFile')}
          </Text>
        </HStack>}
    </HStack>;
  const writeComponent = onChange != null && !readonly ? <DropZone variant="file" getRootProps={getRootProps} getInputProps={getInputProps} isDragActive={isDragActive} acceptedFiles={[]} onResetButtonClick={() => {}} uploadCompleted={true} uploadError={undefined} fileItems={fileItems} onRemoveFile={handleRemoveFile} disabled={readonly} helpText={helpText} onHighlyConfidentialFileDownload={(fileItem: FileItem) => {
    if ('url' in fileItem.file) {
      onHighlyConfidentialFileDownload({
        s3Url: fileItem.file.url,
        originalName: fileItem.name,
        mimeType: fileItem.file.type
      });
    }
  }} /> : undefined;
  return <>
      <CFormItemBase {...props} readComponent={readComponent} writeComponent={writeComponent} />
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <SecureGuard requirement="mfa" enforcement="always" onCancel={() => setOpen(false)} onAuthSuccess={() => {
          setIsSecured(true);
          fileDownload?.();
          setOpen(false);
        }}>
            <></>
          </SecureGuard>
        </DialogContent>
      </Dialog>
    </>;
});